import apiClient from '../utils/apiClient';

/**
 * Shift API Services
 * Quản lý ca làm việc, đăng ký ca và duyệt ca
 */

export const shiftAPI = {
  /**
   * Lấy danh sách ca làm việc với phân trang và lọc
   * @param {Object} params - Tham số truy vấn
   * @param {string} params.branch_id - ID chi nhánh
   * @param {string} params.employee_id - ID nhân viên
   * @param {string} params.date_from - Ngày bắt đầu (YYYY-MM-DD)
   * @param {string} params.date_to - Ngày kết thúc (YYYY-MM-DD)
   * @param {string} params.status - Trạng thái ca (PENDING, APPROVED, COMPLETED, CANCELLED)
   * @param {number} params.page - Trang hiện tại
   * @param {number} params.limit - Số lượng mỗi trang
   * @returns {Promise<Object>} Danh sách ca và metadata phân trang
   */
  getShifts: async (params = {}) => {
    const {
      branch_id = '',
      employee_id = '',
      date_from = '',
      date_to = '',
      status = '',
      page = 1,
      limit = 10,
    } = params;

    return await apiClient.get({
      url: '/shifts',
      params: { branch_id, employee_id, date_from, date_to, status, page, limit },
    });
  },

  /**
   * Lấy thông tin chi tiết ca làm việc theo ID
   * @param {string} shiftId - ID ca làm việc
   * @returns {Promise<Object>} Thông tin chi tiết ca
   */
  getShiftById: async (shiftId) => {
    return await apiClient.get({
      url: `/shifts/${shiftId}`,
    });
  },

  /**
   * Lấy toàn bộ dữ liệu review ca (shift + tasks + orders + vouchers + reconciliation)
   * @param {string} shiftId - ID ca làm việc
   * @returns {Promise<Object>} Aggregated review data
   */
  getShiftReviewDetail: async (shiftId) => {
    return await apiClient.get({
      url: `/shifts/${shiftId}/review-detail`,
    });
  },

  /**
   * Lấy danh sách ca cần review (tổng quan cho admin)
   * @param {Object} params - Tham số truy vấn
   * @param {string} params.branch_id - ID chi nhánh
   * @param {string} params.employee_id - ID nhân viên
   * @param {string} params.date_from - Ngày bắt đầu (YYYY-MM-DD)
   * @param {string} params.date_to - Ngày kết thúc (YYYY-MM-DD)
   * @param {string} params.status - Comma-separated statuses
   * @param {number} params.page - Trang hiện tại
   * @param {number} params.limit - Số lượng mỗi trang
   * @returns {Promise<Object>} { data, summary, meta }
   */
  getShiftReviewList: async (params = {}) => {
    const {
      branch_id = '',
      employee_id = '',
      date_from = '',
      date_to = '',
      status = '',
      page = 1,
      limit = 20,
    } = params;
    return await apiClient.get({
      url: '/shifts/review-list',
      params: { branch_id, employee_id, date_from, date_to, status, page, limit },
    });
  },

  /**
   * Lấy danh sách ca của nhân viên cụ thể
   * @param {string} employeeId - ID nhân viên
   * @param {Object} params - Tham số truy vấn
   * @returns {Promise<Object>} Danh sách ca của nhân viên
   */
  getEmployeeShifts: async (employeeId, params = {}) => {
    const { date_from = '', date_to = '', status = '', page = 1, limit = 10 } = params;
    return await apiClient.get({
      url: `/shifts/employee/${employeeId}`,
      params: { date_from, date_to, status, page, limit },
    });
  },

  /**
   * Đăng ký ca làm việc mới
   * @param {Object} shiftData - Dữ liệu ca làm việc
   * @param {string} shiftData.branch_id - ID chi nhánh
   * @param {string} shiftData.start_time - Thời gian bắt đầu (ISO string)
   * @param {string} shiftData.end_time - Thời gian kết thúc (ISO string)
   * @param {string} shiftData.template_id - ID template ca (optional)
   * @param {string} shiftData.current_user_id - ID người dùng hiện tại
   * @returns {Promise<Object>} Thông tin ca vừa đăng ký
   */
  createShift: async (shiftData) => {
    return await apiClient.post({
      url: '/shifts',
      data: shiftData,
    });
  },

  /**
   * Duyệt ca làm việc (MANAGER|ADMIN)
   * @param {string} shiftId - ID ca làm việc
   * @param {string} managerId - ID quản lý duyệt
   * @returns {Promise<Object>} Thông tin ca sau khi duyệt
   */
  approveShift: async (shiftId, managerId) => {
    return await apiClient.post({
      url: `/shifts/${shiftId}/approve`,
      data: { current_user_id: managerId },
    });
  },

  /**
   * Từ chối ca làm việc (MANAGER|ADMIN)
   * @param {string} shiftId - ID ca làm việc
   * @param {string} managerId - ID quản lý từ chối
   * @param {string} reason - Lý do từ chối
   * @returns {Promise<Object>} Thông tin ca sau khi từ chối
   */
  rejectShift: async (shiftId, managerId, reason) => {
    return await apiClient.post({
      url: `/shifts/${shiftId}/reject`,
      data: { current_user_id: managerId, reason },
    });
  },

  /**
   * #191746 — Hủy từ chối ca (bấm nhầm "Từ chối"): ca về lại chờ duyệt, hoặc chờ xác nhận nếu
   * đã chấm công ra (MANAGER|ADMIN)
   * @param {string} shiftId - ID ca làm việc
   * @param {string} managerId - ID quản lý đang bấm (máy chủ ưu tiên danh tính trong phiên đăng nhập)
   */
  undoRejectShift: async (shiftId, managerId) => {
    return await apiClient.post({
      url: `/shifts/${shiftId}/undo-reject`,
      data: { current_user_id: managerId },
    });
  },

  /**
   * #015110 — Duyệt HÀNG LOẠT ca trong 1 transaction (MANAGER|ADMIN).
   *
   * Khác hẳn cách cũ (gọi approveShift N lần rời rạc từ trình duyệt): backend kiểm hết trước
   * rồi mới ghi, có 1 ca hỏng thì KHÔNG ca nào được duyệt và trả 400 kèm
   * `details.failures = [{ shift_id, reason }]` để biết chính xác ca nào phải xử tay.
   *
   * @param {string[]} shiftIds - Danh sách id ca cần duyệt
   * @param {string} managerId - ID quản lý đang bấm duyệt
   */
  bulkApproveShifts: async (shiftIds = [], managerId) => {
    return await apiClient.post({
      url: '/shifts/bulk-approve',
      data: { shift_ids: shiftIds, ...(managerId ? { current_user_id: managerId } : {}) },
    });
  },

  /**
   * Duyệt ca tăng cường (MANAGER|ADMIN)
   * @param {string} shiftId - ID ca tăng cường
   * @param {string} managerId - ID quản lý duyệt
   * @returns {Promise<Object>} Thông tin ca sau khi duyệt
   */
  approveOvertime: async (shiftId, managerId) => {
    return await apiClient.post({
      url: `/shifts/${shiftId}/approve-overtime`,
      data: { current_user_id: managerId },
    });
  },

  /**
   * Từ chối ca tăng cường (MANAGER|ADMIN)
   * @param {string} shiftId - ID ca tăng cường
   * @param {string} managerId - ID quản lý từ chối
   * @param {string} reason - Lý do từ chối
   * @returns {Promise<Object>} Thông tin ca sau khi từ chối
   */
  rejectOvertime: async (shiftId, managerId, reason) => {
    return await apiClient.post({
      url: `/shifts/${shiftId}/reject-overtime`,
      data: { current_user_id: managerId, reason },
    });
  },

  /**
   * Hủy duyệt ca (rollback từ APPROVED về PENDING) (MANAGER|ADMIN)
   * @param {string} shiftId - ID ca làm việc
   * @returns {Promise<Object>} Thông tin ca sau khi hủy duyệt
   */
  revokeApproval: async (shiftId) => {
    return await apiClient.post({
      url: `/shifts/${shiftId}/revoke-approval`,
    });
  },

  /**
   * Trigger auto approve ca cho một tuần (bot tự động duyệt PENDING → APPROVE_BOT)
   * @param {string} weekStart - Ngày bắt đầu tuần (YYYY-MM-DD), mặc định thứ Hai tuần tới
   * @param {string} branchId - ID chi nhánh (tùy chọn)
   */
  autoApprove: async (weekStart, branchId) => {
    return await apiClient.post({
      url: '/shifts/auto-approve',
      data: {
        ...(weekStart ? { week_start: weekStart } : {}),
        ...(branchId ? { branch_id: branchId } : {}),
      },
    });
  },

  generateAutoApproveProposals: async ({ weekStart, branchId, options = 5 } = {}) => {
    return await apiClient.post({
      url: '/shifts/auto-approve/proposals',
      data: {
        ...(weekStart ? { week_start: weekStart } : {}),
        ...(branchId ? { branch_id: branchId } : {}),
        options,
      },
    });
  },

  // #133908 — bộ điều khiển yếu tố đề xuất của bot (đọc/ghi theo chi nhánh).
  getBotFactorSettings: async ({ branchId } = {}) => {
    return await apiClient.get({
      url: '/shifts/auto-approve/factor-settings',
      params: { ...(branchId ? { branch_id: branchId } : {}) },
    });
  },

  saveBotFactorSettings: async ({
    branchId,
    factors,
    blockNewInHighRevenue,
    revenuePriorityEnabled,
  } = {}) => {
    return await apiClient.put({
      url: '/shifts/auto-approve/factor-settings',
      data: {
        ...(branchId ? { branch_id: branchId } : {}),
        ...(factors ? { factors } : {}),
        ...(typeof blockNewInHighRevenue === 'boolean'
          ? { block_new_in_high_revenue: blockNewInHighRevenue }
          : {}),
        ...(typeof revenuePriorityEnabled === 'boolean'
          ? { revenue_priority_enabled: revenuePriorityEnabled }
          : {}),
      },
    });
  },

  getAutoApproveFloorSuggestions: async ({
    weekStart,
    branchId,
    proposalId,
    minShifts,
  } = {}) => {
    return await apiClient.post({
      url: '/shifts/auto-approve/floor-suggestions',
      data: {
        ...(weekStart ? { week_start: weekStart } : {}),
        ...(branchId ? { branch_id: branchId } : {}),
        ...(proposalId ? { proposal_id: proposalId } : {}),
        ...(typeof minShifts === 'number' ? { min_shifts: minShifts } : {}),
      },
    });
  },

  applyAutoApproveProposal: async (shiftIds = []) => {
    return await apiClient.post({
      url: '/shifts/auto-approve/apply',
      data: { shift_ids: shiftIds },
    });
  },

  // NOTE: các hàm auto-approve/revoke-all này thường được gọi qua withBranch(),
  // vốn inject "branch_id" (snake_case) vào params — nên phải destructure branch_id,
  // không phải branchId, nếu không giá trị chi nhánh sẽ luôn undefined và request
  // sẽ áp dụng cho TẤT CẢ chi nhánh thay vì chỉ chi nhánh đang chọn.
  revokeAllAutoApproveBot: async ({ weekStart, branch_id } = {}) => {
    return await apiClient.post({
      url: '/shifts/auto-approve/revoke-all',
      data: {
        ...(weekStart ? { week_start: weekStart } : {}),
        ...(branch_id ? { branch_id } : {}),
      },
    });
  },

  revokeAllAutoApproved: async ({ weekStart, branch_id } = {}) => {
    return await apiClient.post({
      url: '/shifts/auto-approve/revoke-all',
      data: {
        ...(weekStart ? { week_start: weekStart } : {}),
        ...(branch_id ? { branch_id } : {}),
      },
    });
  },

  /**
   * Revoke toàn bộ ca đã duyệt của tuần về PENDING, kể cả ca duyệt tay (APPROVED)
   * chứ không chỉ APPROVE_BOT — dùng khi cần reset sạch cả tuần để test lại.
   */
  revokeAllApprovedIncludingManual: async ({ weekStart, branch_id } = {}) => {
    return await apiClient.post({
      url: '/shifts/auto-approve/revoke-all',
      data: {
        include_manual: true,
        ...(weekStart ? { week_start: weekStart } : {}),
        ...(branch_id ? { branch_id } : {}),
      },
    });
  },

  /**
   * Hủy ca làm việc (nhân viên tự hủy)
   * @param {string} shiftId - ID ca làm việc
   * @param {string} userId - ID người dùng hiện tại
   * @returns {Promise<Object>} Thông tin ca sau khi hủy
   */
  cancelShift: async (shiftId, userId) => {
    return await apiClient.patch({
      url: `/shifts/${shiftId}/cancel`,
      data: { current_user_id: userId },
    });
  },

  /**
   * Cập nhật ca làm việc
   * @param {string} shiftId - ID ca làm việc
   * @param {Object} updateData - Dữ liệu cập nhật
   * @returns {Promise<Object>} Thông tin ca sau khi cập nhật
   */
  updateShift: async (shiftId, updateData) => {
    return await apiClient.patch({
      url: `/shifts/${shiftId}`,
      data: updateData,
    });
  },

  /**
   * Tạo nhiều ca cùng lúc (MANAGER|ADMIN)
   * @param {Object} bulkData - Dữ liệu tạo ca hàng loạt
   * @param {string} bulkData.current_user_id - ID người dùng hiện tại
   * @param {Array} bulkData.items - Danh sách ca cần tạo
   * @returns {Promise<Object>} Kết quả tạo ca hàng loạt
   */
  createBulkShifts: async (bulkData) => {
    return await apiClient.post({
      url: '/shifts/bulk',
      data: bulkData,
    });
  },

  /**
   * Lấy danh sách slot ca trong ngày
   * @param {string} branchId - ID chi nhánh
   * @param {string} date - Ngày (YYYY-MM-DD)
   * @returns {Promise<Array>} Danh sách slot ca
   */
  getShiftSlots: async (branchId, date) => {
    return await apiClient.get({
      url: '/shifts/slots',
      params: { branch_id: branchId, date },
    });
  },

  /**
   * Lấy danh sách nhân viên có thể đăng ký ca theo template và thời gian
   * @param {string} templateId - ID template ca
   * @param {string} startTime - Thời gian bắt đầu (ISO string)
   * @param {string} endTime - Thời gian kết thúc (ISO string)
   * @returns {Promise<Object>} Danh sách nhân viên có sẵn với thông tin ca ưu tiên
   */
  getAvailableEmployees: async (templateId, startTime, endTime) => {
    return await apiClient.get({
      url: '/shifts/available-employees',
      params: { template_id: templateId, start_time: startTime, end_time: endTime },
    });
  },

  /**
   * Lấy lịch ca theo tháng
   * @param {string} month - Tháng (YYYY-MM)
   * @param {string} branchId - ID chi nhánh (optional)
   * @returns {Promise<Object>} Lịch ca theo tháng
   */
  getShiftCalendar: async (month, branchId = '') => {
    return await apiClient.get({
      url: '/shifts/calendar',
      params: { month, branch_id: branchId },
    });
  },

  /**
   * Lấy thống kê ca làm việc
   * @param {Object} params - Tham số truy vấn
   * @param {string} params.branch_id - ID chi nhánh
   * @param {string} params.employee_id - ID nhân viên
   * @param {string} params.month - Tháng (YYYY-MM)
   * @returns {Promise<Object>} Thống kê ca làm việc
   */
  getShiftStats: async (params = {}) => {
    const { branch_id = '', employee_id = '', month = '' } = params;
    return await apiClient.get({
      url: '/shifts/stats',
      params: { branch_id, employee_id, month },
    });
  },

  /**
   * Kiểm tra các ngày trong tháng còn thiếu nhân viên
   * @param {string} branchId - ID chi nhánh
   * @param {string} month - Tháng (YYYY-MM)
   * @returns {Promise<Object>} Danh sách ngày thiếu nhân viên
   */
  checkCoverage: async (branchId, month) => {
    return await apiClient.get({
      url: '/shifts/coverage-check',
      params: { branch_id: branchId, month },
    });
  },

  /**
   * Lấy danh sách ca được nhóm theo template, sắp xếp theo số lượng đăng ký (ít nhất trước)
   * @param {Object} params - Tham số truy vấn
   * @param {string} params.branch_id - ID chi nhánh
   * @param {string} params.status - Trạng thái ca (PENDING, APPROVED, etc.)
   * @param {number} params.page - Trang hiện tại
   * @param {number} params.limit - Số lượng mỗi trang
   * @returns {Promise<Object>} Danh sách ca nhóm theo template và metadata phân trang
   */
  getShiftsGroupedByTemplate: async (params = {}) => {
    const { branch_id = '', status = 'PENDING', page = 1, limit = 100, date_from = '', date_to = '' } = params;
    return await apiClient.get({
      url: '/shifts/grouped-by-template',
      params: { branch_id, status, page, limit, date_from, date_to },
    });
  },

  /**
   * Lấy danh sách ca trống (chưa đủ người đăng ký)
   * @param {Object} params - Tham số truy vấn
   * @param {string} params.branch_id - ID chi nhánh
   * @param {string} params.date - Ngày cụ thể (YYYY-MM-DD) - phải có date, month hoặc week
   * @param {string} params.month - Tháng (YYYY-MM) - phải có date, month hoặc week
   * @param {string} params.week - Ngày bất kỳ trong tuần (YYYY-MM-DD) - phải có date, month hoặc week
   * @returns {Promise<Array>} Danh sách ca trống
   */
  getEmptySlots: async (params = {}) => {
    const { branch_id = '', date = '', month = '', week = '' } = params;
    return await apiClient.get({
      url: '/shifts/empty-slots',
      params: { branch_id, date, month, week },
    });
  },

  /**
   * Set boost thưởng cho ca trống
   * @param {Object} data - Dữ liệu boost
   * @param {string} data.branch_id - ID chi nhánh
   * @param {string} data.template_id - ID template ca
   * @param {string} data.date - Ngày (YYYY-MM-DD)
   * @param {number} data.reward_rate_bonus - Tỉ lệ thưởng boost (%)
   * @param {string} data.current_user_id - ID admin/manager tạo boost
   * @returns {Promise<Object>} Thông tin boost đã tạo/update
   */
  setSlotBoost: async (data) => {
    return await apiClient.post({
      url: '/shifts/slot-boost',
      data,
    });
  },

  /**
   * Xóa boost thưởng cho ca trống
   * @param {Object} data - Dữ liệu xóa boost
   * @param {string} data.branch_id - ID chi nhánh
   * @param {string} data.template_id - ID template ca
   * @param {string} data.date - Ngày (YYYY-MM-DD)
   * @returns {Promise<Object>} Kết quả xóa
   */
  removeSlotBoost: async (data) => {
    return await apiClient.post({
      url: '/shifts/slot-boost/remove',
      data,
    });
  },
};
